import { randomUUID } from "./uuid";

/**
 * Sanitize an arbitrary string into a valid, lowercase git branch fragment.
 * Strips quotes, collapses separators, limits to `MAX_BRANCH_FRAGMENT_LENGTH`
 * chars, cutting at the nearest word boundary rather than mid-word so a long
 * title (a Plane issue's ref+title, a pasted sentence, ...) yields a readable
 * name like `taskin/fix-the-login` instead of one truncated mid-phrase.
 */
export const WORKTREE_BRANCH_PREFIX = "taskin";
const LEGACY_WORKTREE_BRANCH_PREFIXES = ["dpcode"] as const;
const TEMP_WORKTREE_BRANCH_PATTERN = new RegExp(
  `^(${WORKTREE_BRANCH_PREFIX}|${LEGACY_WORKTREE_BRANCH_PREFIXES.join("|")})\\/[0-9a-f]{8}$`,
);

const MAX_BRANCH_FRAGMENT_LENGTH = 48;

/**
 * Cut `value` (already hyphen-separated) to at most `maxLength` chars,
 * preferring the last hyphen boundary at or before the limit so words stay
 * whole. Falls back to a hard cut when the boundary would discard more than
 * half the budget (e.g. a single word longer than the whole limit).
 */
function truncateAtWordBoundary(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }
  const hardCut = value.slice(0, maxLength);
  const boundary = hardCut.lastIndexOf("-");
  return boundary >= maxLength / 2 ? hardCut.slice(0, boundary) : hardCut;
}

export function sanitizeBranchFragment(raw: string): string {
  const normalized = raw
    .trim()
    .toLowerCase()
    // Fold diacritics first: git branches are ASCII-only here, and without
    // this every accented letter would become a separator — "título" would
    // read "t-tulo" rather than "titulo".
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['"`]/g, "")
    .replace(/^[./\s_-]+|[./\s_-]+$/g, "");

  const collapsed = normalized
    .replace(/[^a-z0-9/_-]+/g, "-")
    .replace(/\/+/g, "/")
    .replace(/-+/g, "-")
    .replace(/^[./_-]+|[./_-]+$/g, "");

  const branchFragment = truncateAtWordBoundary(collapsed, MAX_BRANCH_FRAGMENT_LENGTH).replace(
    /[./_-]+$/g,
    "",
  );

  return branchFragment.length > 0 ? branchFragment : "update";
}

/**
 * Sanitize a string into a `feature/…` branch name.
 * Preserves an existing `feature/` prefix or slash-separated namespace.
 */
export function sanitizeFeatureBranchName(raw: string): string {
  const sanitized = sanitizeBranchFragment(raw);
  if (sanitized.includes("/")) {
    return sanitized.startsWith("feature/") ? sanitized : `feature/${sanitized}`;
  }
  return `feature/${sanitized}`;
}

const AUTO_FEATURE_BRANCH_FALLBACK = "feature/update";

/**
 * Resolve a unique `feature/…` branch name that doesn't collide with
 * any existing branch. Appends a numeric suffix when needed.
 */
export function resolveAutoFeatureBranchName(
  existingBranchNames: readonly string[],
  preferredBranch?: string,
): string {
  const preferred = preferredBranch?.trim();
  const resolvedBase = sanitizeFeatureBranchName(
    preferred && preferred.length > 0 ? preferred : AUTO_FEATURE_BRANCH_FALLBACK,
  );
  const existingNames = new Set(existingBranchNames.map((branch) => branch.toLowerCase()));

  if (!existingNames.has(resolvedBase)) {
    return resolvedBase;
  }

  let suffix = 2;
  while (existingNames.has(`${resolvedBase}-${suffix}`)) {
    suffix += 1;
  }

  return `${resolvedBase}-${suffix}`;
}

export function isTemporaryWorktreeBranch(branch: string): boolean {
  return TEMP_WORKTREE_BRANCH_PATTERN.test(branch.trim().toLowerCase());
}

export function buildTemporaryWorktreeBranchName(): string {
  return buildTemporaryWorktreeBranchParts().branch;
}

/**
 * Build a temporary worktree branch name together with its raw 8-char token.
 *
 * Callers that place the worktree under a per-project directory prefer the
 * bare token (to avoid repeating the `taskin/` prefix in both the branch and
 * the filesystem path).
 */
export function buildTemporaryWorktreeBranchParts(): { token: string; branch: string } {
  const token = randomUUID().replace(/-/g, "").slice(0, 8).toLowerCase();
  return { token, branch: `${WORKTREE_BRANCH_PREFIX}/${token}` };
}

/**
 * Build a worktree branch name from a human-supplied (or LLM-generated) label:
 * `taskin/<slug>`. Tolerates a `refs/heads/` prefix and an already-present
 * `taskin/` prefix so callers can pass raw git output or raw user input.
 *
 * Note the contrast with {@link buildTemporaryWorktreeBranchParts}: a named
 * branch does NOT match {@link isTemporaryWorktreeBranch}, which is what stops
 * the first-turn LLM rename from overwriting a name the user chose.
 */
export function buildNamedWorktreeBranchName(raw: string): string {
  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/^refs\/heads\//, "");
  const withoutPrefix = normalized.startsWith(`${WORKTREE_BRANCH_PREFIX}/`)
    ? normalized.slice(`${WORKTREE_BRANCH_PREFIX}/`.length)
    : normalized;
  return `${WORKTREE_BRANCH_PREFIX}/${sanitizeBranchFragment(withoutPrefix)}`;
}

/** Segment that marks a branch as owned by a persistent environment. */
const ENVIRONMENT_BRANCH_SEGMENT = "env";

/**
 * Build the permanent branch of a persistent environment: `taskin/env-<slug>`.
 *
 * Three properties are load-bearing:
 *
 * - It does **not** match {@link isTemporaryWorktreeBranch}, so the automatic
 *   first-turn rename can never rewrite it. An environment's branch is its
 *   identity for as long as the environment exists.
 * - It is derived only from the owner's slug, so it is stable and reproducible:
 *   re-provisioning the same pair lands on the same name.
 * - It shares the `taskin/` namespace, so it stays inside the prefix the rest
 *   of the system already treats as ours (and out of the user's own branches).
 */
export function buildEnvironmentBranchName(userSlug: string): string {
  // Empty input is handled before sanitizing: `sanitizeBranchFragment` answers
  // "update" for anything that sanitizes away, which as a branch name would
  // read as a thread about an update rather than as somebody's environment.
  const trimmed = userSlug.trim();
  const suffix = trimmed.length > 0 ? sanitizeBranchFragment(trimmed).replace(/\//g, "-") : "user";
  return `${WORKTREE_BRANCH_PREFIX}/${ENVIRONMENT_BRANCH_SEGMENT}-${suffix}`;
}

/**
 * Build the permanent branch of a *project* environment: `taskin/env-qa`.
 *
 * Shares the shape of {@link buildEnvironmentBranchName} deliberately, rather
 * than the `env/<name>` of the design note: {@link isEnvironmentWorktreeBranch}
 * is what tells the rest of the system "this branch is an environment's
 * identity, never rename it, never garbage-collect it", and it recognises
 * exactly this prefix. A branch outside it would be protected by nothing.
 *
 * Collisions with a personal environment's branch are possible only in theory —
 * personal ones are built from an email, which always carries its domain — and
 * they fail loudly when they happen, because git refuses to create a worktree
 * on a branch another worktree already holds.
 */
export function buildProjectEnvironmentBranchName(name: string): string {
  const trimmed = name.trim();
  const suffix = trimmed.length > 0 ? sanitizeBranchFragment(trimmed).replace(/\//g, "-") : "env";
  return `${WORKTREE_BRANCH_PREFIX}/${ENVIRONMENT_BRANCH_SEGMENT}-${suffix}`;
}

/** Whether a branch is the permanent branch of a persistent environment. */
export function isEnvironmentWorktreeBranch(branch: string): boolean {
  return branch
    .trim()
    .toLowerCase()
    .startsWith(`${WORKTREE_BRANCH_PREFIX}/${ENVIRONMENT_BRANCH_SEGMENT}-`);
}

/**
 * Sanitize an arbitrary string (typically `path.basename` of a project root)
 * for use as a filesystem directory segment under `worktrees/`. Strips
 * separators and falls back to `"project"` on empty input.
 */
export function sanitizeProjectDirname(raw: string): string {
  const sanitized = sanitizeBranchFragment(raw).replace(/\//g, "-");
  return sanitized.length > 0 ? sanitized : "project";
}

// Preserve semantic thread branches when transient worktree placeholders briefly
// appear in git status during rename/bootstrap transitions.
export function resolveThreadBranchRegressionGuard(input: {
  currentBranch: string | null;
  nextBranch: string | null;
}): string | null {
  if (
    input.currentBranch !== null &&
    input.nextBranch !== null &&
    !isTemporaryWorktreeBranch(input.currentBranch) &&
    isTemporaryWorktreeBranch(input.nextBranch)
  ) {
    return input.currentBranch;
  }

  return input.nextBranch;
}
